import { useState, useMemo, useEffect, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  useStudyGroupSources,
  useCreateStudyGroup,
  useQuestionsFeed,
  useClassUploads,
  useClassFriendUploads,
  useFriends,
  useMe,
  type StudyGroupSource,
} from '../lib/api';
import { buildCategoryIndex } from '../lib/topic-tags';
import { motion, FadeIn, Pressable, PageTransition, Modal, Skeleton, springs } from '../lib/motion';

type SourceType = 'class_section' | 'general_exam';
type Difficulty = 'easy' | 'hard';
type Speed = 'rapid' | 'slow';
type Scope = 'all' | 'mine' | 'friends';

// Per-question time depends on BOTH difficulty and pace: easy/quick questions are
// shorter, hard MCQs get more time to read and reason.
const TIMER_SECONDS: Record<Difficulty, Record<Speed, number>> = {
  easy: { rapid: 5, slow: 15 },
  hard: { rapid: 10, slow: 20 },
};

const STEP_LABELS = ['Difficulty', 'Pace', 'Source', 'Questions'];

export function StudyGroupCreatePage() {
  const navigate = useNavigate();

  // Study groups are Pro-only; bounce non-Pro who deep-link here.
  const me = useMe();
  useEffect(() => {
    if (me.data && me.data.user.proAccess === false) navigate('/study-groups', { replace: true });
  }, [me.data, navigate]);

  const [step, setStep] = useState(1);
  const [difficulty, setDifficulty] = useState<Difficulty | null>(null);
  const [speed, setSpeed] = useState<Speed | null>(null);
  const [source, setSource] = useState<(StudyGroupSource & { type: SourceType }) | null>(null);

  // Question-picker state (mirrors regular study sessions)
  const [scope, setScope] = useState<Scope>('all');
  const [showFriendsUpsell, setShowFriendsUpsell] = useState(false);
  const [selectedUploadIds, setSelectedUploadIds] = useState<Set<string>>(new Set());
  const [selectedTopics, setSelectedTopics] = useState<Set<string>>(new Set());
  const [questionCount, setQuestionCount] = useState(20);

  const questionType: 'mcq' | 'quick_mcq' = difficulty === 'easy' ? 'quick_mcq' : 'mcq';
  const timerSeconds = difficulty && speed ? TIMER_SECONDS[difficulty][speed] : 15;
  const isClass = source?.type === 'class_section';

  const { data: sourcesData, isLoading: sourcesLoading } = useStudyGroupSources();
  const sources: Array<StudyGroupSource & { type: SourceType }> = useMemo(() => {
    if (!sourcesData) return [];
    const classes = (sourcesData.classes ?? []).map((s) => ({ ...s, type: 'class_section' as const }));
    const exams = (sourcesData.exams ?? []).map((s) => ({ ...s, type: 'general_exam' as const }));
    return [...classes, ...exams];
  }, [sourcesData]);

  // Feed for the chosen source (only fetched once a source is picked)
  const { data: feed } = useQuestionsFeed(
    source ? (isClass ? { class_section_id: source.id } : { exam_id: source.id }) : {},
  );
  const { data: friendsData } = useFriends();
  const friendIds = useMemo(
    () => new Set((friendsData?.friends ?? []).filter((f) => f.status === 'accepted').map((f) => f.id)),
    [friendsData],
  );
  const { data: uploadsData } = useClassUploads(isClass && source ? { class_section_id: source.id } : {});
  const { data: friendUploadsData } = useClassFriendUploads(isClass && source ? { class_section_id: source.id } : {});
  const myUploads = uploadsData?.uploads ?? [];
  const friendUploads = friendUploadsData?.uploads ?? [];
  const canUseFriendDecks = feed?.entitlements?.canUseFriendDecks ?? false;

  const createMutation = useCreateStudyGroup();

  // Pool of the chosen type
  const typedPool = useMemo(
    () => (feed?.questions ?? []).filter((q: any) => q.type === questionType),
    [feed?.questions, questionType],
  );

  const applyScope = (pool: any[]): any[] => {
    let p = pool;
    if (scope === 'mine' && feed?.currentUserId) {
      p = p.filter((q: any) => q.authorUserId === feed.currentUserId);
    } else if (scope === 'friends') {
      p = p.filter((q: any) => q.authorUserId && friendIds.has(q.authorUserId));
    }
    if (scope !== 'all' && selectedUploadIds.size > 0) {
      p = p.filter((q: any) => q.ingestionJobId && selectedUploadIds.has(q.ingestionJobId));
    }
    return p;
  };

  const scopedPool = useMemo(
    () => applyScope(typedPool),
    [typedPool, scope, feed?.currentUserId, friendIds, selectedUploadIds],
  );

  // Topic index over the scoped pool (mcq uses rich categories; quick uses payload.topic)
  const categoryIndex = useMemo(() => {
    if (questionType === 'mcq') return buildCategoryIndex(scopedPool);
    const idx = new Map<string, Set<string>>();
    for (const q of scopedPool) {
      const topic = (q.payload as any)?.topic || 'General';
      let set = idx.get(topic);
      if (!set) { set = new Set(); idx.set(topic, set); }
      set.add(q.id);
    }
    return idx;
  }, [scopedPool, questionType]);

  const availableTopics = useMemo(
    () => Array.from(categoryIndex.entries()).sort((a, b) => a[0].localeCompare(b[0])).map(([name, ids]) => ({ name, count: ids.size })),
    [categoryIndex],
  );
  const allTopics = selectedTopics.size === 0;

  const filteredPool = useMemo(() => {
    if (allTopics) return scopedPool;
    const ids = new Set<string>();
    for (const t of selectedTopics) categoryIndex.get(t)?.forEach((id) => ids.add(id));
    return scopedPool.filter((q) => ids.has(q.id));
  }, [scopedPool, allTopics, selectedTopics, categoryIndex]);

  const available = filteredPool.length;
  const maxQuestions = Math.min(50, available);
  const effectiveCount = Math.min(Math.max(5, questionCount), Math.max(5, maxQuestions));

  const counts = useMemo(() => ({
    all: typedPool.length,
    mine: typedPool.filter((q: any) => q.authorUserId === feed?.currentUserId).length,
    friends: typedPool.filter((q: any) => q.authorUserId && friendIds.has(q.authorUserId)).length,
  }), [typedPool, feed?.currentUserId, friendIds]);

  const toggleUpload = (id: string) =>
    setSelectedUploadIds((prev) => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const toggleTopic = (name: string) =>
    setSelectedTopics((prev) => { const n = new Set(prev); n.has(name) ? n.delete(name) : n.add(name); return n; });
  const resetPicker = () => { setScope('all'); setSelectedUploadIds(new Set()); setSelectedTopics(new Set()); };

  const handleSubmit = async () => {
    if (!source || !difficulty || !speed) return;
    const candidateQuestionIds = filteredPool.map((q) => q.id);
    if (candidateQuestionIds.length < 5) return;
    try {
      const result = await createMutation.mutateAsync({
        sourceType: source.type,
        sourceId: source.id,
        questionType,
        candidateQuestionIds,
        topicFilter: allTopics ? null : Array.from(selectedTopics),
        questionCount: effectiveCount,
        timerSeconds,
      });
      navigate(`/study-groups/${result.group.id}/lobby`);
    } catch { /* error shown via mutation state */ }
  };

  const handleBack = () => {
    if (step === 1) navigate('/study-groups');
    else setStep(step - 1);
  };

  const activeUploads = scope === 'friends' ? friendUploads : myUploads;
  const uploadCountFor = (u: any) => (questionType === 'quick_mcq' ? u.quickCount : u.questionCount);

  return (
    <PageTransition className="max-w-lg mx-auto pb-24 px-4">
      <div className="flex items-center gap-3 mb-5">
        <Pressable onClick={handleBack} className="flex h-10 w-10 -ml-2 items-center justify-center rounded-full text-slate-500 hover:text-squishy-dark transition-colors" aria-label="Go back">
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
          </svg>
        </Pressable>
        <div>
          <h1 className="text-xl font-bold font-display text-squishy-dark">Create Study Group</h1>
          <p className="text-[11px] text-slate-400 mt-0.5">Step {step} of 4 · {STEP_LABELS[step - 1]}</p>
        </div>
      </div>

      {/* Step indicator */}
      <div className="flex items-center gap-2 mb-6" aria-hidden>
        {[1, 2, 3, 4].map((s) => (
          <div key={s} className="flex items-center gap-2 flex-1">
            <motion.div
              animate={s === step ? { scale: 1.08 } : { scale: 1 }}
              transition={springs.snappy}
              className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold transition-colors ${
                s < step
                  ? 'bg-squishy-teal/15 text-squishy-primary'
                  : s === step
                    ? 'bg-squishy-primary text-white shadow-soft'
                    : 'bg-slate-100 text-slate-400'
              }`}
            >
              {s < step ? (
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                </svg>
              ) : s}
            </motion.div>
            {s < 4 && (
              <div className="relative flex-1 h-1 rounded-full bg-slate-200 overflow-hidden">
                <motion.div
                  className="absolute inset-y-0 left-0 bg-squishy-primary rounded-full"
                  initial={false}
                  animate={{ width: s < step ? '100%' : '0%' }}
                  transition={springs.soft}
                />
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Step 1: Difficulty */}
      {step === 1 && (
        <FadeIn className="space-y-3">
          <h2 className="text-sm font-semibold text-squishy-dark">Choose your challenge</h2>
          <p className="text-xs text-slate-500">Quick Recall is fast, one-line questions. Exam Style is full multiple choice.</p>
          <ChoiceCard
            title="Quick Recall"
            subtitle="Quick Study format: short one-line questions, tap the answer, no explanations."
            icon={<BoltIcon />}
            active={difficulty === 'easy'}
            accent="gold"
            onClick={() => { setDifficulty('easy'); resetPicker(); setStep(2); }}
          />
          <ChoiceCard
            title="Exam Style"
            subtitle="Full multiple-choice questions from your class or exam (MCQ only)."
            icon={<BookIcon />}
            active={difficulty === 'hard'}
            accent="teal"
            onClick={() => { setDifficulty('hard'); resetPicker(); setStep(2); }}
          />
        </FadeIn>
      )}

      {/* Step 2: Speed */}
      {step === 2 && (
        <FadeIn className="space-y-3">
          <h2 className="text-sm font-semibold text-squishy-dark">Set the pace</h2>
          <p className="text-xs text-slate-500">How long does everyone get to answer each question?</p>
          <ChoiceCard
            title="Rapid Fire"
            subtitle={`${difficulty === 'hard' ? 10 : 5} seconds per question. Trust your instincts.`}
            icon={<FlameIcon />}
            active={speed === 'rapid'}
            accent="red"
            onClick={() => { setSpeed('rapid'); setStep(3); }}
          />
          <ChoiceCard
            title="Steady Pace"
            subtitle={`${difficulty === 'hard' ? 20 : 15} seconds per question. Room to reason it through.`}
            icon={<ClockIcon />}
            active={speed === 'slow'}
            accent="teal"
            onClick={() => { setSpeed('slow'); setStep(3); }}
          />
        </FadeIn>
      )}

      {/* Step 3: Source */}
      {step === 3 && (
        <FadeIn className="space-y-3">
          <h2 className="text-sm font-semibold text-squishy-dark">Choose a class or exam</h2>
          <p className="text-xs text-slate-500">Where should the questions come from?</p>
          {sourcesLoading ? (
            <div className="space-y-2">
              <Skeleton className="h-16 w-full rounded-2xl" />
              <Skeleton className="h-16 w-full rounded-2xl" />
              <Skeleton className="h-16 w-full rounded-2xl" />
            </div>
          ) : sources.length === 0 ? (
            <div className="text-center py-12 text-slate-400 text-sm">No sources available. Enroll in a class or browse exams first.</div>
          ) : (
            <div className="space-y-2">
              {sources.map((s) => (
                <Pressable
                  key={`${s.type}-${s.id}`}
                  onClick={() => { setSource(s); resetPicker(); setQuestionCount(20); setStep(4); }}
                  className="w-full bg-white border border-slate-200 rounded-2xl p-4 text-left shadow-card transition-colors hover:border-squishy-teal/60"
                >
                  <div className="flex items-center justify-between gap-3">
                    <div className="min-w-0">
                      <div className="text-sm font-semibold text-squishy-dark truncate">{s.name}</div>
                      <div className="text-xs text-slate-500 mt-0.5">{s.type === 'class_section' ? 'Class' : 'Exam'}</div>
                    </div>
                    <svg className="w-4 h-4 text-slate-400 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                    </svg>
                  </div>
                </Pressable>
              ))}
            </div>
          )}
        </FadeIn>
      )}

      {/* Step 4: Question picker */}
      {step === 4 && source && (
        <FadeIn className="space-y-4">
          <div>
            <h2 className="text-sm font-semibold text-squishy-dark">Choose questions</h2>
            <p className="text-xs text-slate-500 mt-0.5">
              {difficulty === 'easy' ? 'Quick Recall' : 'Exam Style'} {'·'} {timerSeconds}s per question {'·'} {source.name}
            </p>
          </div>

          {/* Source tabs */}
          <div className="flex bg-slate-100 rounded-xl p-1 gap-1">
            {(['all', 'mine', 'friends'] as Scope[]).map((sc) => {
              const locked = sc === 'friends' && !canUseFriendDecks;
              return (
                <button
                  key={sc}
                  onClick={() => {
                    if (locked) { setShowFriendsUpsell(true); return; }
                    setScope(sc); setSelectedUploadIds(new Set()); setSelectedTopics(new Set());
                  }}
                  className={`relative flex-1 py-2 min-h-[44px] text-[11px] font-semibold rounded-lg transition-all inline-flex items-center justify-center gap-1 ${scope === sc ? 'bg-white text-squishy-primary shadow-card' : 'text-slate-500'} ${locked ? 'opacity-70' : ''}`}
                >
                  {locked && (
                    <svg viewBox="0 0 20 20" fill="currentColor" className="w-3 h-3 text-squishy-gold">
                      <path fillRule="evenodd" d="M10 1a4.5 4.5 0 00-4.5 4.5V9H5a2 2 0 00-2 2v6a2 2 0 002 2h10a2 2 0 002-2v-6a2 2 0 00-2-2h-.5V5.5A4.5 4.5 0 0010 1zm3 8V5.5a3 3 0 10-6 0V9h6z" clipRule="evenodd" />
                    </svg>
                  )}
                  {sc === 'all' ? 'All' : sc === 'mine' ? 'My Q' : 'Friends Q'} ({counts[sc]})
                </button>
              );
            })}
          </div>

          {/* Upload chips (class sources only) */}
          {isClass && scope !== 'all' && activeUploads.filter((u) => uploadCountFor(u) > 0).length > 0 && (
            <div>
              <div className="text-[10px] font-semibold text-slate-400 uppercase tracking-wide mb-1.5">
                {scope === 'friends' ? "Friends' uploads" : 'From specific uploads'} {selectedUploadIds.size > 0 ? `(${selectedUploadIds.size} selected)` : '(all)'}
              </div>
              <div className="max-h-28 overflow-y-auto space-y-1.5 pr-1">
                {activeUploads.filter((u) => uploadCountFor(u) > 0).map((u) => (
                  <Pressable
                    key={u.id}
                    onClick={() => toggleUpload(u.id)}
                    className={`w-full flex items-center justify-between px-3 py-2.5 rounded-xl border text-left transition-colors ${selectedUploadIds.has(u.id) ? 'border-squishy-teal bg-squishy-teal/10 text-squishy-primary' : 'border-slate-200 bg-white text-slate-600'}`}
                  >
                    <span className="text-xs font-medium truncate">
                      {scope === 'friends' && u.authorName ? <span className="text-slate-500">{u.authorName} · </span> : null}{u.title}
                      {scope === 'friends' && u.createdAt ? (
                        <span className="text-slate-400 font-normal"> · {new Date(u.createdAt).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: '2-digit' })}</span>
                      ) : null}
                    </span>
                    <span className="text-[10px] text-slate-400 shrink-0 ml-2">{uploadCountFor(u)}</span>
                  </Pressable>
                ))}
              </div>
            </div>
          )}

          {/* Topics */}
          <div className="space-y-2 max-h-56 overflow-y-auto pr-0.5">
            <Pressable
              onClick={() => setSelectedTopics(new Set())}
              className={`w-full flex items-center justify-between px-4 py-3 rounded-2xl border-2 transition-colors ${allTopics ? 'border-squishy-teal bg-squishy-teal/10 text-squishy-primary shadow-glow' : 'border-slate-200 bg-white text-slate-600'}`}
            >
              <span className="font-semibold text-sm">All Topics</span>
              <span className="text-xs text-slate-400">{scopedPool.length}</span>
            </Pressable>
            {availableTopics.map((t) => (
              <Pressable
                key={t.name}
                onClick={() => toggleTopic(t.name)}
                className={`w-full flex items-center justify-between px-4 py-3 rounded-2xl border-2 transition-colors ${selectedTopics.has(t.name) ? 'border-squishy-teal bg-squishy-teal/10 text-squishy-primary' : 'border-slate-200 bg-white text-slate-600'}`}
              >
                <span className="font-medium text-left text-sm">{t.name}</span>
                <span className="text-xs text-slate-400 shrink-0 ml-2">{t.count}</span>
              </Pressable>
            ))}
          </div>

          {/* Count */}
          <div className="bg-white border border-slate-200 rounded-2xl p-4 space-y-3 shadow-card">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-slate-700">Questions</span>
              <span className="text-base font-bold font-display text-squishy-primary">{effectiveCount} <span className="text-xs font-normal text-slate-400">/ {available}</span></span>
            </div>
            <input
              type="range"
              min={5}
              max={Math.max(5, maxQuestions)}
              step={1}
              value={effectiveCount}
              onChange={(e) => setQuestionCount(Number(e.target.value))}
              disabled={available < 5}
              aria-label="Number of questions"
              className="w-full accent-squishy-primary"
            />
          </div>

          {available < 5 && (
            <p className="text-xs text-red-500">Not enough questions in this selection (need at least 5). Try All or a different filter.</p>
          )}
          {createMutation.isError && (
            <p className="text-xs text-red-500">{(createMutation.error as Error)?.message || 'Failed to create group.'}</p>
          )}

          <Pressable
            haptic="medium"
            onClick={handleSubmit}
            disabled={createMutation.isPending || available < 5}
            className="w-full bg-squishy-primary text-white font-semibold py-3.5 rounded-2xl disabled:opacity-40 shadow-soft transition-opacity"
          >
            {createMutation.isPending ? 'Creating...' : `Create Group (${effectiveCount} questions)`}
          </Pressable>
        </FadeIn>
      )}

      {/* Friends' decks upsell (Plus+) */}
      <Modal open={showFriendsUpsell} onClose={() => setShowFriendsUpsell(false)}>
        <div className="bg-white rounded-3xl w-full p-6 text-center shadow-pop">
          <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-2xl bg-squishy-gold/20">
            <svg viewBox="0 0 20 20" fill="currentColor" className="w-6 h-6 text-squishy-gold">
              <path fillRule="evenodd" d="M10 1a4.5 4.5 0 00-4.5 4.5V9H5a2 2 0 00-2 2v6a2 2 0 002 2h10a2 2 0 002-2v-6a2 2 0 00-2-2h-.5V5.5A4.5 4.5 0 0010 1zm3 8V5.5a3 3 0 10-6 0V9h6z" clipRule="evenodd" />
            </svg>
          </div>
          <h3 className="text-base font-bold font-display text-squishy-dark">Use your friends' questions</h3>
          <p className="mt-2 text-sm text-slate-600 leading-relaxed">
            Build a study group from the questions your friends uploaded. This is a <span className="font-semibold">Plus &amp; Pro</span> feature.
          </p>
          <Pressable
            haptic="medium"
            onClick={() => { setShowFriendsUpsell(false); navigate('/profile'); }}
            className="mt-5 block w-full rounded-2xl bg-squishy-primary py-3 text-sm font-semibold text-white shadow-soft"
          >
            Upgrade to Plus (from $1.99/mo)
          </Pressable>
          <Pressable onClick={() => setShowFriendsUpsell(false)} className="mt-2 w-full py-2.5 text-sm text-slate-400">
            Maybe later
          </Pressable>
        </div>
      </Modal>
    </PageTransition>
  );
}

function ChoiceCard({ title, subtitle, icon, active, accent, onClick }: {
  title: string; subtitle: string; icon: ReactNode; active: boolean;
  accent: 'gold' | 'teal' | 'red'; onClick: () => void;
}) {
  const hover = accent === 'gold' ? 'hover:border-squishy-gold/60' : accent === 'red' ? 'hover:border-red-300' : 'hover:border-squishy-teal/60';
  const chip = accent === 'gold'
    ? 'bg-squishy-gold/15 text-amber-600'
    : accent === 'red'
      ? 'bg-red-50 text-red-500'
      : 'bg-squishy-teal/10 text-squishy-primary';
  return (
    <Pressable
      haptic="light"
      onClick={onClick}
      className={`w-full bg-white border-2 rounded-2xl p-4 text-left shadow-card transition-colors ${active ? 'border-squishy-teal shadow-glow' : `border-slate-200 ${hover}`}`}
    >
      <div className="flex items-start gap-3">
        <div className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-xl ${chip}`}>
          {icon}
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-bold text-squishy-dark">{title}</div>
          <div className="text-xs text-slate-500 mt-0.5 leading-relaxed">{subtitle}</div>
        </div>
        <svg className="w-4 h-4 text-slate-400 mt-1 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
        </svg>
      </div>
    </Pressable>
  );
}

function BoltIcon() {
  return (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M13 10V3L4 14h7v7l9-11h-7z" />
    </svg>
  );
}

function BookIcon() {
  return (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
    </svg>
  );
}

function FlameIcon() {
  return (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M17.657 18.657A8 8 0 016.343 7.343S7 9 9 10c0-2 .5-5 2.986-7C14 5 16.09 5.777 17.656 7.343A7.975 7.975 0 0120 13a7.975 7.975 0 01-2.343 5.657z" />
      <path strokeLinecap="round" strokeLinejoin="round" d="M9.879 16.121A3 3 0 1012.015 11L11 14H9c0 .768.293 1.536.879 2.121z" />
    </svg>
  );
}

function ClockIcon() {
  return (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  );
}
